import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ChevronRight, Loader2, RefreshCw } from 'lucide-react';

interface Props {
    token: string;
}

const getApiBaseUrl = () => {
    let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

export default function ScheduledJobsRunLogsTab({ token }: Props) {
    const [runs, setRuns] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
    const [runDetails, setRunDetails] = useState<Record<string, any[]>>({});
    const [runDetailsLoading, setRunDetailsLoading] = useState<string | null>(null);

    useEffect(() => {
        fetchRuns();
    }, [token]);

    const fetchRuns = async () => {
        setLoading(true);
        setExpandedRunId(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/saas/schedules/membership-status/runs?limit=25`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setRuns(data.success ? data.data : []);
        } catch {
            toast.error('Failed to load run log');
        } finally {
            setLoading(false);
        }
    };

    const toggleRunDetails = async (runId: string) => {
        if (expandedRunId === runId) { setExpandedRunId(null); return; }
        setExpandedRunId(runId);
        if (runDetails[runId]) return; // cached
        setRunDetailsLoading(runId);
        try {
            const res = await fetch(`${API_BASE_URL}/api/saas/schedules/membership-status/runs/${runId}/details`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setRunDetails(prev => ({ ...prev, [runId]: data.success ? data.data : [] }));
        } catch {
            toast.error('Failed to load run details');
        } finally {
            setRunDetailsLoading(null);
        }
    };

    return (
        <div>
            <div className="mb-4 flex items-center justify-between gap-4">
                <div>
                    <h3 className="text-base font-semibold text-gray-900">Membership Status — Run Log</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Expand a run to see which memberships changed. These logs are super-admin only.</p>
                </div>
                <button
                    onClick={fetchRuns}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            <div className="bg-white shadow overflow-hidden sm:rounded-md">
                {loading ? (
                    <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
                ) : runs.length === 0 ? (
                    <div className="text-center py-16 text-sm text-gray-500">No runs yet. Use “Run Now” on the Jobs tab to trigger one.</div>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {runs.map((run) => {
                            const isOpen = expandedRunId === run.id;
                            const details = runDetails[run.id] || [];
                            return (
                                <li key={run.id}>
                                    <button
                                        onClick={() => toggleRunDetails(run.id)}
                                        className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50 text-left"
                                    >
                                        <div className="flex items-center gap-3">
                                            <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {run.startTime ? new Date(run.startTime).toLocaleString() : '—'}
                                                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">{run.triggerType}</span>
                                                </p>
                                                <p className="text-xs text-gray-500">{run.totalCount ?? 0} changed</p>
                                            </div>
                                        </div>
                                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${run.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : run.status === 'FAILED' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{run.status}</span>
                                    </button>
                                    {isOpen && (
                                        <div className="px-6 pb-4 bg-gray-50/60">
                                            {run.errorDetails && <p className="text-xs text-red-600 mb-2">Error: {run.errorDetails}</p>}
                                            {runDetailsLoading === run.id ? (
                                                <div className="flex items-center gap-2 py-4 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading details…</div>
                                            ) : details.length === 0 ? (
                                                <p className="text-xs text-gray-500 py-3">No membership changes in this run.</p>
                                            ) : (
                                                <table className="w-full text-xs mt-1">
                                                    <thead>
                                                        <tr className="text-left text-gray-400 uppercase tracking-wider">
                                                            <th className="py-1.5 pr-3">Member</th>
                                                            <th className="py-1.5 pr-3">Plan</th>
                                                            <th className="py-1.5 pr-3">Change</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {details.map((d, i) => (
                                                            <tr key={i}>
                                                                <td className="py-1.5 pr-3 font-medium text-gray-800">{d.memberName || d.memberId || '—'}</td>
                                                                <td className="py-1.5 pr-3 text-gray-500">{d.planName || '—'}</td>
                                                                <td className="py-1.5 pr-3">
                                                                    <span className="text-gray-500">{d.from || '—'}</span>
                                                                    <span className="mx-1 text-gray-400">→</span>
                                                                    <span className={`font-semibold ${d.to === 'active' ? 'text-green-700' : 'text-gray-700'}`}>{d.to || '—'}</span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
